import { SentrySettings } from './api/dtos';

export interface ISentrySettings {
    // These two fields are env-var-only; everything else comes from the API via AppSettingsContext.
    application: string;
    release?: string;
}

export interface IAppSettings {
    stripePublicKey: string;
    sentry: ISentrySettings;
    printingApi: string;
    BartenderCloudBaseApi: string;
}

export const AppSettings: IAppSettings = {
    stripePublicKey: import.meta.env.STRIPE_PUBLIC_KEY,
    sentry: {
        application: import.meta.env.VITE_SENTRY_APPLICATION ?? 'Conference',
        release: import.meta.env.VITE_SENTRY_RELEASE,
    },
    printingApi: "http://localhost:5159/api/actions?KeepStatus=60m&Wait=15s&MessageCount=200&MessageSeverity=Info",
    BartenderCloudBaseApi: "http://localhost/BarTender/api/v1/"
}

const getSentryEnvironmentValue = (
    environmentProperty: string | undefined
): string | undefined => {
    if (!environmentProperty) {
        return undefined;
    }
    const value: string | undefined = import.meta.env[environmentProperty];
    if (value === undefined || value === null || value === "") {
        return undefined;
    }
    return value;
};

const getSentryEnvironmentString = (
    environmentProperty: string | undefined
): string | undefined => {
    return getSentryEnvironmentValue(environmentProperty);
};

const getSentryEnvironmentNumber = (
    environmentProperty: string | undefined
): number | undefined => {
    const valueStr = getSentryEnvironmentValue(environmentProperty);
    if (!valueStr) {
        return undefined;
    }
    try {
        return parseFloat(valueStr);
    }
    catch {
        return undefined;
    }
};

const getSentryEnvironmentBoolean = (
    environmentProperty: string | undefined
): boolean | undefined => {
    const valueStr = getSentryEnvironmentValue(environmentProperty);
    if (!valueStr) {
        return undefined;
    }
    const lowerCase = valueStr.toLowerCase();
    if (lowerCase === "1" || lowerCase === 'true') {
        return true;
    }
    if (lowerCase === "0" || lowerCase === 'false') {
        return false;
    }
    return undefined;
};

/**
 * Builds a SentrySettings from Vite env vars.
 * Returns undefined when VITE_SENTRY_DSN is not set, which means Sentry
 * will not be initialized (the expected behaviour in most dev environments).
 * Used in main.tsx as a fallback when window.APPSETTINGS is not populated
 * (i.e. outside of a ServiceStack-rendered environment).
 */
export const getSentrySettingsFromEnv = (): SentrySettings | undefined => {
    const dsn = getSentryEnvironmentString('VITE_SENTRY_DSN');
    if (!dsn) {
        return undefined;
    }

    return new SentrySettings({
        dsn,
        environment:              getSentryEnvironmentString('VITE_SENTRY_ENVIRONMENT') ?? 'Development',
        customer:                 getSentryEnvironmentString('VITE_SENTRY_CUSTOMER') ?? 'Unknown',
        debug:                    getSentryEnvironmentBoolean('VITE_SENTRY_DEBUG') ?? false,
        sendDefaultPii:           getSentryEnvironmentBoolean('VITE_SENTRY_SEND_DEFAULT_PII') ?? false,
        enableMetrics:            getSentryEnvironmentBoolean('VITE_SENTRY_ENABLE_METRICS') ?? false,
        attachStacktrace:         getSentryEnvironmentBoolean('VITE_SENTRY_ATTACH_STACKTRACE') ?? false,
        enableLogs:               getSentryEnvironmentBoolean('VITE_SENTRY_ENABLE_LOGS') ?? false,
        enableReportABug:         getSentryEnvironmentBoolean('VITE_SENTRY_ENABLE_REPORT_A_BUG') ?? false,
        tracesSampleRate:         getSentryEnvironmentNumber('VITE_SENTRY_TRACE_SAMPLE_RATE') ?? 0.2,
        replaysSessionSampleRate: getSentryEnvironmentNumber('VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE') ?? 0.1,
        replaysOnErrorSampleRate: getSentryEnvironmentNumber('VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE') ?? 1.0,
    });
};